import type { ReactNode } from "react";
import type { LeaveRequest } from "@/api/types";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { FiledLateBadge, StatusBadge } from "@/components/ui/badge";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { formatDate, formatTimestampDate } from "@/lib/format";
import { leaveLabel } from "@/lib/labels";

/**
 * One leave request, in full.
 *
 * This exists because of the reason field. In a table the reason has to be
 * truncated to keep the row on one line, and a truncated reason is worse than
 * no reason at all — it is exactly long enough to look like the whole thing
 * while omitting the part that decides the answer. An approver was being asked
 * to approve something they could not read.
 *
 * So the table keeps its one-line summary and this holds the real content:
 * the complete reason, unclipped and wrapped, plus the surrounding facts an
 * approver would otherwise have to reconstruct from three other columns.
 *
 * Read-only on purpose. Approve/decline stay in the table where the queue is,
 * so a decision is never one stray click away from the thing you opened to read.
 */
export function LeaveDetailDialog({
  request,
  onClose,
}: {
  request: LeaveRequest;
  onClose: () => void;
}) {
  const spansOneDay = request.start_date === request.end_date;
  const person = request.employee_name;
  const withdrawnCount = request.withdrawn_days?.length ?? 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={leaveLabel(request.leave_type)}
          description={
            spansOneDay
              ? formatDate(request.start_date) ?? request.start_date
              : `${formatDate(request.start_date)} to ${formatDate(request.end_date)}`
          }
        />

        <DialogBody className="flex flex-col gap-5">
          {person && (
            <div className="flex items-center gap-2.5">
              <Avatar name={person} size="sm" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{person}</p>
                {request.team_name && (
                  <p className="truncate text-xs text-muted">{request.team_name}</p>
                )}
              </div>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Detail label="Status">
              <span className="flex flex-wrap items-center gap-1.5">
                <StatusBadge status={request.status} />
                <FiledLateBadge request={request} />
              </span>
            </Detail>
            <Detail label="Working days">
              {/* The stored span and the days it costs are different numbers —
                  weekends and holidays inside a span are not deducted — so the
                  figure is spelled out rather than left to be counted off the
                  dates above. */}
              <span className="text-sm text-ink">
                {request.days} {request.days === 1 ? "day" : "days"}
                {withdrawnCount > 0 && (
                  <span className="text-muted"> of {request.approved_days}</span>
                )}
              </span>
            </Detail>
            <Detail label="Applied on">
              <span className="font-mono text-xs text-ink-soft">
                {formatTimestampDate(request.created_at) ?? "—"}
              </span>
            </Detail>
            <Detail label={request.status === "pending" ? "Decision" : "Decided"}>
              {request.status === "pending" ? (
                <span className="text-sm text-muted">Not yet decided</span>
              ) : (
                <span className="text-sm text-ink-soft">
                  {request.decided_by_name ?? "—"}
                  {request.decided_at && (
                    <span className="ml-1 font-mono text-xs text-muted">
                      {formatTimestampDate(request.decided_at, "d MMM")}
                    </span>
                  )}
                </span>
              )}
            </Detail>
          </dl>

          {withdrawnCount > 0 && (
            <div className="rounded-lg border border-line bg-surface-sunken/50 px-3 py-2.5">
              <p className="text-xs font-medium text-muted">Withdrawn</p>
              <p className="mt-1 text-sm leading-relaxed text-ink-soft">
                {withdrawnCount} {withdrawnCount === 1 ? "day was" : "days were"} handed back and
                returned to the balance:{" "}
                <span className="font-mono text-xs text-ink">
                  {(request.withdrawn_days ?? [])
                    .map((date) => formatDate(date, "d MMM"))
                    .join(", ")}
                </span>
              </p>
            </div>
          )}

          <div>
            <p className="mb-1.5 text-xs font-medium text-muted">Reason</p>
            {request.reason ? (
              // whitespace-pre-wrap keeps the line breaks the person typed, and
              // break-words stops one long unbroken string from widening the
              // dialog past the viewport.
              <p className="whitespace-pre-wrap break-words rounded-lg border border-line bg-surface-sunken/50 px-3 py-2.5 text-sm leading-relaxed text-ink">
                {request.reason}
              </p>
            ) : (
              <p className="text-sm text-muted">
                {request.leave_type === "OptionalHoliday"
                  ? "Optional Holiday needs no reason — the date is the request."
                  : "No reason was given."}
              </p>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="mb-1 text-xs font-medium text-muted">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/**
 * The table cell that opens the dialog.
 *
 * A real focusable button, not a click handler on the row: a `tr` cannot carry
 * button semantics without breaking the table for a screen reader, so this is
 * what makes the reason reachable by keyboard. Rows may additionally be
 * clickable for the mouse, which is a convenience layered on top of this.
 */
export function ReasonCell({
  reason,
  onOpen,
  className,
}: {
  reason: string | null;
  onOpen: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      title={reason ?? undefined}
      className={
        "block max-w-full truncate text-left text-ink-soft underline-offset-4 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface " +
        (className ?? "")
      }
    >
      {reason ?? "—"}
    </button>
  );
}
